import { loadUserSnapshot } from "@/utils/userSessionCache"

type AuthRoutePrefetchState = {
  isBackendMode: boolean
  isInitialized: boolean
}

const cachedUser = loadUserSnapshot()

let authRoutePrefetchState: AuthRoutePrefetchState = {
  isBackendMode: !!cachedUser,
  isInitialized: !!cachedUser,
}

/**
 * Updates the auth snapshot used by route loaders before React context is available.
 *
 * @param nextState - Latest auth state needed to decide whether backend prefetch is safe.
 */
export const setAuthRoutePrefetchState = (nextState: AuthRoutePrefetchState) => {
  authRoutePrefetchState = nextState
}

/**
 * Returns whether a route loader should prefetch backend data.
 *
 * Loaders execute before AuthProvider mounts, so they rely on this module-level
 * snapshot instead of React context. Prefetching is allowed only after auth has
 * finished initializing and confirmed that backend mode is active.
 *
 * @returns True when route prefetching should use backend queries.
 */
export const shouldPrefetchBackendData = () =>
  authRoutePrefetchState.isInitialized && authRoutePrefetchState.isBackendMode

/**
 * Runs a route prefetch only when backend mode has already been established.
 *
 * @param prefetch - Query prefetch callback.
 * @returns The prefetch result when backend mode is active; otherwise undefined.
 */
export const runBackendModePrefetch = <T>(prefetch: () => T): T | undefined => {
  if (!shouldPrefetchBackendData()) {
    return undefined
  }

  return prefetch()
}
